import avaturnCustomAnimationConfig from '../config/avaturnCustomAnimationConfig';
import { AVATURN_ANIMATED_FACE_MORPH_NAMES } from '../constants/emotionBlendshapes';

/**
 * Morphs the custom animation is allowed to write.
 *
 * Deliberately the same set avaturn_animation owns: the emotion overlay skips
 * these (isAvaturnAnimatedFaceMorph) and viseme15 lipsync never zeroes them
 * (LIPSYNC_PRESERVE_NAMES), so the custom animation contends with neither. Derived from the
 * shared constant rather than re-listed, so the two cannot drift apart.
 */
export const CUSTOM_ANIMATION_MORPH_NAMES = [
  ...AVATURN_ANIMATED_FACE_MORPH_NAMES
];

/** Bones the breathing offset rotates. The head is deliberately left alone. */
export const CUSTOM_ANIMATION_BONE_NAMES = [
  'Spine1',
  'Spine2',
  'Neck',
  'LeftShoulder',
  'RightShoulder'
];

const DEG = Math.PI / 180;

const randomBetween = (random, min, max) => min + random() * (max - min);

/** FNV-1a. Stable across reloads, so the same avatar always animates the same way. */
export function hashCustomAnimationSeed(key) {
  const text = String(key ?? '');
  let hash = 2166136261;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0 || 1;
}

/**
 * Deterministic mulberry32. Two avatars that resolve to the same model get the
 * same seed, so the shared-GLB case writes one set of values instead of two
 * fighting phases, while distinct avatars stay independent.
 */
export function createSeededRandom(key) {
  let state = hashCustomAnimationSeed(key);

  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 0 to 1 and back over one breath; inhale takes 40% of the cycle, exhale 60%. */
export function breathCurve(phase) {
  const p = ((phase % 1) + 1) % 1;

  return p < 0.4
    ? 0.5 - 0.5 * Math.cos((Math.PI * p) / 0.4)
    : 0.5 + 0.5 * Math.cos((Math.PI * (p - 0.4)) / 0.6);
}

/** Fast close (~80ms), short hold, slower open (~150ms). Returns 0..1. */
export function blinkCurve(t) {
  if (t < 0) return 0;
  if (t < 0.08) return t / 0.08;
  if (t < 0.11) return 1;
  if (t < 0.26) return 1 - (t - 0.11) / 0.15;

  return 0;
}

/**
 * Per-avatar custom animation state. The first blink and first saccade are seeded from the
 * random source so avatars standing together never fall into lockstep.
 */
export function createCustomAnimationState(
  random = Math.random,
  config = avaturnCustomAnimationConfig
) {
  return {
    random,
    time: 0,
    blink: 0,
    blinkStart: -1,
    doubleBlink: false,
    nextBlink: randomBetween(random, 1, 3),
    gazeX: 0,
    gazeY: config.gaze.restY,
    gazeTargetX: 0,
    gazeTargetY: config.gaze.restY,
    nextSaccade: randomBetween(random, 0.5, 2),
    // Breathing is seeded per avatar too. A shared start phase and rate would
    // have every character in a scene inhaling and exhaling together.
    breathPhase: random(),
    breathRate:
      config.breathRate *
      (1 +
        randomBetween(
          random,
          -config.breathRateJitter,
          config.breathRateJitter
        ))
  };
}

/** Steps breathing, blink scheduling and the damped gaze by one frame. Mutates state. */
export function advanceCustomAnimationState(
  state,
  delta,
  config = avaturnCustomAnimationConfig
) {
  const random = state.random ?? Math.random;
  const step = Math.min(Math.max(delta, 0), config.maxDelta);

  state.time += step;
  const t = state.time;

  if (state.blinkStart < 0 && t >= state.nextBlink) {
    state.blinkStart = t;
    state.doubleBlink = random() < config.blink.doubleChance;
  }

  let blink = 0;
  if (state.blinkStart >= 0) {
    const elapsed = t - state.blinkStart;
    blink =
      blinkCurve(elapsed) +
      (state.doubleBlink ? blinkCurve(elapsed - config.blink.doubleDelay) : 0);

    const closes = state.doubleBlink ? config.blink.doubleDelay + 0.3 : 0.3;
    if (elapsed > closes) {
      state.blinkStart = -1;
      state.nextBlink =
        t +
        randomBetween(
          random,
          config.blink.interval[0],
          config.blink.interval[1]
        );
    }
  }
  state.blink = Math.min(1, blink) * config.blink.strength;

  if (t >= state.nextSaccade) {
    // Often settle back towards centre, the way a person does between glances.
    if (random() < config.gaze.recentreChance) {
      state.gazeTargetX = 0;
      state.gazeTargetY = config.gaze.restY;
    } else {
      state.gazeTargetX = randomBetween(
        random,
        -config.gaze.range.x,
        config.gaze.range.x
      );
      state.gazeTargetY =
        config.gaze.restY +
        randomBetween(random, -config.gaze.range.y, config.gaze.range.y);
    }
    state.nextSaccade =
      t +
      randomBetween(random, config.gaze.interval[0], config.gaze.interval[1]);
  }

  // Framerate-independent easing, matching the emotion overlay's convention.
  const ease = 1 - Math.exp(-config.gaze.damping * step);
  state.gazeX += (state.gazeTargetX - state.gazeX) * ease;
  state.gazeY += (state.gazeTargetY - state.gazeY) * ease;

  return state;
}

/**
 * Absolute values for every morph in CUSTOM_ANIMATION_MORPH_NAMES, zeros included, so the
 * custom animation fully owns its channels instead of leaving stale weights behind.
 * Writes into `target` when given, so the per-frame path allocates nothing.
 */
export function computeCustomAnimationMorphValues(
  state,
  config = avaturnCustomAnimationConfig,
  target = {}
) {
  const { blink, gazeX, gazeY } = state;

  // Two slow sines, so the brows wander instead of sitting frozen.
  const drift =
    Math.sin(state.time * 0.37) * 0.5 + Math.sin(state.time * 0.91 + 1.3) * 0.5;

  target.eyeBlinkLeft = blink;
  target.eyeBlinkRight = blink;

  const eyeSquint =
    config.restingFace.eyeSquint + blink * config.restingFace.squintOnBlink;
  target.eyeSquintLeft = eyeSquint;
  target.eyeSquintRight = eyeSquint;

  const browDown = Math.max(
    0,
    config.restingFace.browDown - drift * config.restingFace.browDrift
  );
  target.browDownLeft = browDown;
  target.browDownRight = browDown;

  // Fade the gaze out as the lids close: an eye flicking behind a closed lid is
  // the most uncanny thing a custom animation can do.
  const aperture = 1 - blink;
  const right = Math.max(0, gazeX) * aperture;
  const left = Math.max(0, -gazeX) * aperture;
  const up = Math.max(0, gazeY) * aperture;
  const down = Math.max(0, -gazeY) * aperture;

  // +x is the character's own left, +y is up.
  target.eyeLookOutLeft = right;
  target.eyeLookInRight = right;
  target.eyeLookInLeft = left;
  target.eyeLookOutRight = left;
  target.eyeLookUpLeft = up;
  target.eyeLookUpRight = up;
  target.eyeLookDownLeft = down;
  target.eyeLookDownRight = down;

  return target;
}

/**
 * X-rotation offsets in radians, keyed by bone name, for the current breath.
 * Writes into `target` when given, so the per-frame path allocates nothing.
 */
export function computeBreathOffsets(
  state,
  config = avaturnCustomAnimationConfig,
  target = {}
) {
  // Per-avatar phase and rate, falling back to the shared base when a caller
  // hands in a bare state.
  const rate = state.breathRate ?? config.breathRate;
  const phase = state.breathPhase ?? 0;
  const breath = breathCurve(phase + (state.time * rate) / 60);
  const chest = breath * config.breathDepth * DEG;
  const lift = -breath * config.shoulderLift * DEG;

  // The neck counters the chest, so the ribs rise without the head nodding.
  target.Spine1 = chest * config.breathWeight.Spine1;
  target.Spine2 = chest * config.breathWeight.Spine2;
  target.Neck = chest * config.breathWeight.Neck;
  target.LeftShoulder = lift;
  target.RightShoulder = lift;

  return target;
}
